import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import client from "@/lib/directus";
import directus from "@/lib/directus";
import { readMe } from "@directus/sdk";
import { UserInfo } from "@/interfaces/user-interface";
import useFindUser from "@/hooks/useFindUser";

// Schéma de validation avec Zod
const formSchema = z.object({
    email: z.string().email("Email invalide"),
    password: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
});

type FormValues = z.infer<typeof formSchema>;

function LoginPage() {
    const [isloading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const { setUser } = useFindUser();

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: "",
            password: "",
        },
    });

    const onSubmit = async (data: FormValues) => {
        setIsLoading(true);
        try {
            const loginResponse = await client.login(data.email, data.password)
            localStorage.setItem('directus_auth', JSON.stringify(loginResponse))
            const currentUser = await directus.request(readMe());
            const newUser = currentUser as UserInfo;
            if (currentUser) {
                setUser(newUser);
                setError(null);
                navigate("/dashboard");
                window.location.reload();
            } else {
                setUser(undefined);
            }

        } catch (error: any) {

            setError(
                error.errors[0].message ||
                "Une erreur est survenue lors de la connexion"
            );
            console.error("Erreur de connexion :", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">

            <div className="w-full max-w-sm">
                <div className={cn("flex flex-col gap-6")}>

                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-2 mb-2">
                                <button
                                    onClick={() => navigate("/")}
                                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Retour à l'accueil
                                </button>
                            </div>
                            <CardTitle className="items-center justify-center text-center text-2xl">
                                <div>Connexion</div>
                            </CardTitle>
                            <CardDescription className="items-center justify-center text-center ">
                                Entrez vos informations pour vous connecter
                            </CardDescription>
                        </CardHeader>

                        <CardContent>
                            {error && (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertTitle>Échec d'authentification</AlertTitle>
                                    <AlertDescription>
                                        ${error.replace("Firebase", "Error")}
                                    </AlertDescription>
                                </Alert>


                            )}
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)}>
                                    <div className="mt-5 flex flex-col gap-6">
                                        <div className="grid gap-2">
                                            <div className="flex items-center">
                                                <Label htmlFor="email">Email</Label>
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name="email"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <Input
                                                                id="email"
                                                                type="email"
                                                                placeholder="m@example.com"
                                                                required
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <FormField
                                                control={form.control}
                                                name="password"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <div className="relative">
                                                                <Input
                                                                    id="password"
                                                                    type={showPassword ? "text" : "password"}
                                                                    required
                                                                    {...field}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="absolute right-2 top-2"
                                                                    onClick={() => setShowPassword(!showPassword)}
                                                                >
                                                                    {showPassword ? (
                                                                        <EyeOff className="h-4 w-4" />
                                                                    ) : (
                                                                        <Eye className="h-4 w-4" />
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <Button type="submit" className="w-full">
                                            {isloading ? <Loader2 className="animate-spin" /> : "Se connecter"}
                                        </Button>

                                        <div className="mt-4 text-center text-sm">
                                            Vous n'avez pas de compte ?{" "}
                                            <a href="/register" className="underline underline-offset-4">
                                                S'inscrire
                                            </a>
                                        </div>
                                    </div>

                                </form>
                            </Form>
                        </CardContent>
                    </Card>
                </div>


            </div>
        </div>
    );
}

export default LoginPage;
